// src/services/sparePartService.js
//
// SPARE PARTS ENGINE — SINGLE GOVERNED SOURCE FOR:
//   1. spare_part_requests   — technician-raised requests (SPR-XXXX)
//   2. part_instances        — serialized parts only (compressor/PCB/motor/
//                              control panel) — permanent, cross-machine record
//   3. part_instance_history — every install/remove/repair/scrap event
//
// RULE (per Constitution — Single Source of Truth / Service Layer):
//   NO UI component may write directly to spare_part_requests,
//   part_instances, or part_instance_history. Every mutation goes
//   through a function in this file.
//
// This engine tracks ONLY serialized parts (compressor/PCB/motor/control
// panel etc. — anything with a unique, physically-labeled serial number).
// Non-serialized consumables (screws, capacitors, wire) are intentionally
// out of scope and should never be raised as a spare part request here.
// is_serialized is hardcoded true on every request created by this file
// (see createSparePartRequest()) — it is no longer a caller-supplied
// decision. The field is retained on existing/legacy docs only for
// backward-compatible display; no code branches on it anymore.

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase";

// ─────────────────────────────────────────────────────────────────────────
// MULTI-TENANT GUARD — application-layer defense in depth.
// Firestore's fallback rule allows READ to any authenticated user
// (isAuth() only, no company check) — only CREATE/UPDATE are protected by
// writingSameCompany()/sameCompany(). That means a cross-tenant requestId
// or partInstanceId could otherwise be fetched and its data used in this
// file's logic even though the eventual write would separately fail.
// Every function below that receives an existing doc ID verifies company
// ownership immediately after fetch, via this guard, before doing anything
// else with the data.
// ─────────────────────────────────────────────────────────────────────────
function assertSameCompany(docData, company_id, label = "record") {
  if (!company_id) throw new Error(`company_id is required to verify ${label} ownership`);
  if (!docData || docData.company_id !== company_id) {
    throw new Error(`Access denied — this ${label} does not belong to your company`);
  }
}

// ─────────────────────────────────────────────────────────────────────────
// NUMBERING — ONE RUNNING SERIES PER COMPANY (P2b, MED-21)
//   SPR-0001 → spare_part_requests.request_no
//   PIN-0001 → part_instances.part_instance_no
// WHY: the counters were global per year (spr_{year}, pin_{year}) — shared
// by every company — and the printed number carries no year, so a yearly
// reset would also repeat SPR-0001 for the SAME company next year. A part's
// PIN is its permanent identity and must never repeat. Now:
// system_counters/spr_{company_id} and pin_{company_id} — one series per
// company, never reset. Printed format unchanged.
// CONTINUITY: on a company's first use the series starts after its own
// highest existing number (read once). The old global docs are history
// only; firestore.rules refuses writes to them.
// A failed save can leave a gap — never a duplicate.
// ─────────────────────────────────────────────────────────────────────────
async function highestExistingNumber(collectionName, fieldName, prefix, company_id) {
  const snap = await getDocs(query(
    collection(db, collectionName),
    where("company_id", "==", company_id)
  ));
  const pattern = new RegExp(`^${prefix}-(\\d+)$`);
  let max = 0;
  snap.docs.forEach((d) => {
    const m = String(d.data()[fieldName] || "").match(pattern);
    if (!m) return;
    const n = parseInt(m[1], 10);
    if (Number.isFinite(n) && n > max) max = n;
  });
  return max;
}

async function reserveCompanyNumber({ counterType, prefix, collectionName, fieldName, company_id }) {
  const companyId = String(company_id || "").trim();
  if (!companyId) throw new Error(`company_id is required to create a ${prefix} number`);

  const counterRef = doc(db, "system_counters", `${counterType}_${companyId}`);

  // Seed read once per company, outside the transaction (a query cannot run
  // inside one). If it fails, nothing is created — never a silent restart.
  const existing = await getDoc(counterRef);
  const seed = existing.exists()
    ? 0
    : await highestExistingNumber(collectionName, fieldName, prefix, companyId);

  const nextNumber = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(counterRef);
    const last = snap.exists() ? Number(snap.data().last_number || 0) : seed;
    const next = last + 1;
    transaction.set(
      counterRef,
      {
        counter_type: counterType,
        company_id:   companyId,
        last_number:  next,
        updated_at:   serverTimestamp(),
      },
      { merge: true }
    );
    return next;
  });

  return `${prefix}-${String(nextNumber).padStart(4, "0")}`;
}

async function generateRequestNumber(company_id) {
  return reserveCompanyNumber({
    counterType:    "spr",
    prefix:         "SPR",
    collectionName: "spare_part_requests",
    fieldName:      "request_no",
    company_id,
  });
}

async function generatePartInstanceNumber(company_id) {
  return reserveCompanyNumber({
    counterType:    "pin",
    prefix:         "PIN",
    collectionName: "part_instances",
    fieldName:      "part_instance_no",
    company_id,
  });
}

// ─────────────────────────────────────────────────────────────────────────
// SECTION 1 — SPARE PART REQUESTS (technician raises, Store/Manager triage)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Technician (or manager, on tech's behalf) raises a new spare part request.
 *
 * @param {Object} params
 * @param {string} params.company_id
 * @param {string} params.service_id        - service_tasks doc id (mandatory)
 * @param {string} [params.project_id]
 * @param {string} params.site_id
 * @param {string} params.client_id
 * @param {string} [params.machine_id]      - site_machines doc id
 * @param {string} params.part_name
 * @param {string} [params.part_no]
 * @param {string} [params.serial_no]       - only if technician already knows it
 * @param {string} [params.brand]
 * @param {number} params.quantity
 * @param {boolean} params.is_serialized    - true => this part will get a
 *                                            part_instances record once resolved
 * @param {string} [params.linked_defective_item_id] - defective_items[] index/id
 *                                            if raised from that section
 * @param {string} [params.notes]
 * @param {string} params.requested_by      - uid
 * @param {string} params.requested_by_name
 */
export async function createSparePartRequest(params) {
  const {
    company_id,
    service_id,
    project_id = "",
    site_id,
    client_id,
    machine_id = "",
    part_name,
    part_no = "",
    serial_no = "",
    brand = "",
    quantity,
    linked_defective_item_id = "",
    notes = "",
    requested_by,
    requested_by_name,
  } = params;

  if (!company_id) throw new Error("company_id is required");
  if (!service_id) throw new Error("service_id is required");
  if (!part_name) throw new Error("part_name is required");
  if (!serial_no || !serial_no.trim()) throw new Error("serial_no is required — this system tracks serialized parts only");
  if (!quantity || quantity < 1) throw new Error("quantity must be at least 1");
  if (!requested_by) throw new Error("requested_by (uid) is required");

  const request_no = await generateRequestNumber(company_id);

  const docRef = await addDoc(collection(db, "spare_part_requests"), {
    company_id,
    request_no,
    service_id,
    project_id,
    site_id,
    client_id,
    machine_id,
    part_name,
    part_no,
    serial_no,
    brand,
    quantity,
    // is_serialized hardcoded true — this system tracks ONLY serialized
    // parts (compressor/PCB/motor/control panel etc.), so every request
    // is serialized by definition. This is no longer a caller-supplied
    // param or a human decision (previously a checkbox in the UI that
    // was easy to forget to tick, causing "Already With Us"/"Send for
    // Repair" to silently never show for that request).
    is_serialized: true,
    linked_defective_item_id,
    notes,
    resolution_type: "pending", // "pending" | "warranty" | "buy" | "already_with_us" | "repair"
    status: "requested", // see STATUS CHAINS below
    linked_part_instance_id: "", // filled in once resolved (serialized parts only)
    linked_po_id: "", // filled in for "buy" branch
    linked_oem_return_id: "", // filled in for "warranty" branch
    requested_by,
    requested_by_name,
    requested_at: serverTimestamp(),
    resolved_by: "",
    resolved_by_name: "",
    resolved_at: null,
  });

  return docRef.id;
}

/**
 * Fetch all spare part requests for a company, optionally filtered by status.
 * company_id is always enforced.
 */
export async function fetchSparePartRequests(company_id, statusFilter = null) {
  if (!company_id) throw new Error("company_id is required");

  const constraints = [where("company_id", "==", company_id)];
  if (statusFilter) {
    constraints.push(where("status", "==", statusFilter));
  }

  const snap = await getDocs(query(collection(db, "spare_part_requests"), ...constraints));

  const results = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Sort client-side (consistent with the enquiries no-orderBy convention —
  // avoids needing a composite Firestore index for every filter combo)
  results.sort((a, b) => {
    const aTime = a.requested_at?.seconds || 0;
    const bTime = b.requested_at?.seconds || 0;
    return bTime - aTime; // newest first
  });

  return results;
}

export async function fetchSparePartRequestById(requestId, company_id) {
  if (!requestId) throw new Error("requestId is required");
  if (!company_id) throw new Error("company_id is required");
  const snap = await getDoc(doc(db, "spare_part_requests", requestId));
  if (!snap.exists()) throw new Error("Spare part request not found");
  const data = { id: snap.id, ...snap.data() };
  assertSameCompany(data, company_id, "spare part request");
  return data;
}

// ─────────────────────────────────────────────────────────────────────────
// SECTION 2 — RESOLUTION BRANCHES (Store/Manager triage — pick ONE of 4)
// ─────────────────────────────────────────────────────────────────────────

/**
 * BRANCH 1 — WARRANTY
 * Links this request to the existing OEM Returns flow. Does NOT create a
 * part_instance yet — that happens only when the OEM actually sends back
 * a replacement (see completeWarrantyReplacement()).
 */
export async function resolveToWarranty({ requestId, company_id, oem_return_id, resolved_by, resolved_by_name }) {
  if (!requestId) throw new Error("requestId is required");
  if (!oem_return_id) throw new Error("oem_return_id is required — link the OEM Returns record first");

  await fetchSparePartRequestById(requestId, company_id); // throws if cross-tenant

  await updateDoc(doc(db, "spare_part_requests", requestId), {
    resolution_type: "warranty",
    status: "sent_to_oem",
    linked_oem_return_id: oem_return_id,
    resolved_by,
    resolved_by_name,
    resolved_at: serverTimestamp(),
  });
}

/**
 * Called once the OEM has actually shipped back a replacement part.
 * If serialized, this is where the part_instance is finally created.
 */
export async function completeWarrantyReplacement({
  requestId,
  company_id,
  received_serial_no,
  machine_id,
  done_by,
  done_by_name,
}) {
  const request = await fetchSparePartRequestById(requestId, company_id); // throws if cross-tenant

  // This system tracks serialized parts only — every request always gets
  // a part_instance created here, regardless of the legacy is_serialized
  // field on the request doc (kept for backward-compat display only).
  if (!received_serial_no || !received_serial_no.trim()) {
    throw new Error("received_serial_no is required");
  }

  const part_instance_id = await createPartInstance({
    company_id: request.company_id,
    part_name: request.part_name,
    part_no: request.part_no,
    brand: request.brand,
    serial_no: received_serial_no || request.serial_no,
    created_from_request_id: requestId,
    source: "warranty",
  });

  if (machine_id) {
    await installPartInstance({
      partInstanceId: part_instance_id,
      company_id: request.company_id,
      machine_id,
      service_id: request.service_id,
      done_by,
      done_by_name,
    });
  }

  await updateDoc(doc(db, "spare_part_requests", requestId), {
    status: "closed",
    linked_part_instance_id: part_instance_id,
  });

  return part_instance_id;
}

/**
 * BRANCH 2 — BUY
 * Links this request to a Purchase Order (direct_site_purchase mode —
 * NO inventory_summary / Item Master impact, cost flows to job costing).
 * The actual part_instance is created at GRN-receive time (see
 * completeBuyReceipt()), once the real serial number is physically confirmed.
 */
export async function resolveToBuy({ requestId, company_id, po_id, resolved_by, resolved_by_name }) {
  if (!requestId) throw new Error("requestId is required");
  if (!po_id) throw new Error("po_id is required — create the PO first (direct_site_purchase mode)");

  await fetchSparePartRequestById(requestId, company_id); // throws if cross-tenant

  await updateDoc(doc(db, "spare_part_requests", requestId), {
    resolution_type: "buy",
    status: "po_created",
    linked_po_id: po_id,
    resolved_by,
    resolved_by_name,
    resolved_at: serverTimestamp(),
  });
}

/**
 * Called at GRN-receive time for a "buy" branch request.
 * received_serial_no is mandatory when the request is is_serialized.
 */
export async function completeBuyReceipt({
  requestId,
  company_id,
  received_serial_no,
  machine_id,
  done_by,
  done_by_name,
}) {
  const request = await fetchSparePartRequestById(requestId, company_id); // throws if cross-tenant

  // This system tracks serialized parts only — received_serial_no is
  // always mandatory at GRN, regardless of the legacy is_serialized
  // field on the request doc (kept for backward-compat display only).
  if (!received_serial_no || !received_serial_no.trim()) {
    throw new Error("received_serial_no is mandatory at GRN");
  }

  const part_instance_id = await createPartInstance({
    company_id: request.company_id,
    part_name: request.part_name,
    part_no: request.part_no,
    brand: request.brand,
    serial_no: received_serial_no,
    created_from_request_id: requestId,
    source: "buy",
  });

  if (machine_id) {
    await installPartInstance({
      partInstanceId: part_instance_id,
      company_id: request.company_id,
      machine_id,
      service_id: request.service_id,
      done_by,
      done_by_name,
    });
  }

  await updateDoc(doc(db, "spare_part_requests", requestId), {
    status: "closed",
    linked_part_instance_id: part_instance_id,
  });

  return part_instance_id;
}

/**
 * BRANCH 3 — ALREADY WITH US
 * Store picks an EXISTING part_instance that is currently "in_store" and
 * installs it directly. Only valid for is_serialized === true requests
 * (non-serialized parts have no instance to pick from — this branch
 * should not be offered to them in the UI).
 */
export async function resolveToAlreadyWithUs({
  requestId,
  company_id,
  partInstanceId,
  machine_id,
  resolved_by,
  resolved_by_name,
}) {
  if (!requestId) throw new Error("requestId is required");
  if (!partInstanceId) throw new Error("partInstanceId is required");

  const instance = await fetchPartInstanceById(partInstanceId, company_id); // throws if cross-tenant

  if (instance.current_status !== "in_store") {
    throw new Error(`Part instance is currently "${instance.current_status}", not "in_store" — cannot select it`);
  }

  const request = await fetchSparePartRequestById(requestId, company_id); // throws if cross-tenant
  const targetMachine = machine_id || request.machine_id;

  await installPartInstance({
    partInstanceId,
    company_id,
    machine_id: targetMachine,
    service_id: request.service_id,
    done_by: resolved_by,
    done_by_name: resolved_by_name,
  });

  await updateDoc(doc(db, "spare_part_requests", requestId), {
    resolution_type: "already_with_us",
    status: "closed",
    linked_part_instance_id: partInstanceId,
    resolved_by,
    resolved_by_name,
    resolved_at: serverTimestamp(),
  });
}

/**
 * BRANCH 4 — SEND FOR REPAIR
 * The OLD, already-installed part_instance (that came OUT of the machine
 * because it broke) goes to external repair. This does not immediately
 * fulfill the request — it fulfills it later, once the repaired part comes
 * back (see completeRepairReturn()).
 *
 * oldPartInstanceId = the instance being sent out (must currently be
 * "installed" — it's coming out of the machine right now).
 */
export async function resolveToRepair({
  requestId,
  company_id,
  oldPartInstanceId,
  resolved_by,
  resolved_by_name,
}) {
  if (!requestId) throw new Error("requestId is required");
  if (!oldPartInstanceId) throw new Error("oldPartInstanceId is required");

  const instance = await fetchPartInstanceById(oldPartInstanceId, company_id); // throws if cross-tenant
  await fetchSparePartRequestById(requestId, company_id); // throws if cross-tenant

  if (instance.current_status !== "installed") {
    throw new Error(`Part instance is currently "${instance.current_status}", not "installed" — cannot send for repair`);
  }

  const removedFromMachine = instance.current_machine_id;

  await updateDoc(doc(db, "part_instances", oldPartInstanceId), {
    current_status: "sent_for_repair",
    current_machine_id: "",
  });

  await logPartInstanceHistory({
    company_id: instance.company_id,
    part_instance_id: oldPartInstanceId,
    event_type: "sent_for_repair",
    machine_id: removedFromMachine,
    service_id: null,
    done_by: resolved_by,
    done_by_name: resolved_by_name,
    notes: "",
  });

  await updateDoc(doc(db, "spare_part_requests", requestId), {
    resolution_type: "repair",
    status: "at_repair_center",
    linked_part_instance_id: oldPartInstanceId,
    resolved_by,
    resolved_by_name,
    resolved_at: serverTimestamp(),
  });
}

/**
 * Called once the repaired part physically comes back.
 * reinstall = true  -> goes straight back into a machine (pass machine_id)
 * reinstall = false -> goes back into store shelf ("in_store")
 */
export async function completeRepairReturn({
  requestId,
  company_id,
  partInstanceId,
  reinstall,
  machine_id,
  done_by,
  done_by_name,
}) {
  const instance = await fetchPartInstanceById(partInstanceId, company_id); // throws if cross-tenant
  const request = await fetchSparePartRequestById(requestId, company_id); // throws if cross-tenant

  await logPartInstanceHistory({
    company_id: instance.company_id,
    part_instance_id: partInstanceId,
    event_type: "returned_from_repair",
    machine_id: reinstall ? machine_id : "",
    service_id: null,
    done_by,
    done_by_name,
    notes: "",
  });

  if (reinstall) {
    if (!machine_id) throw new Error("machine_id is required to reinstall after repair");
    await installPartInstance({
      partInstanceId,
      company_id,
      machine_id,
      service_id: request.service_id,
      done_by,
      done_by_name,
    });
  } else {
    await updateDoc(doc(db, "part_instances", partInstanceId), {
      current_status: "in_store",
      current_machine_id: "",
    });
  }

  await updateDoc(doc(db, "spare_part_requests", requestId), {
    status: "closed",
  });
}

// ─────────────────────────────────────────────────────────────────────────
// SECTION 3 — PART INSTANCES (serialized parts only — permanent lifecycle)
// ─────────────────────────────────────────────────────────────────────────

async function createPartInstance({
  company_id,
  part_name,
  part_no,
  brand,
  serial_no,
  created_from_request_id,
  source, // "warranty" | "buy" | "manual_stock_entry"
}) {
  if (!company_id) throw new Error("company_id is required to create a part_instance");
  if (!serial_no) throw new Error("serial_no is mandatory to create a part_instance");

  const part_instance_no = await generatePartInstanceNumber(company_id);

  const docRef = await addDoc(collection(db, "part_instances"), {
    company_id,
    part_instance_no,
    part_name,
    part_no,
    brand,
    serial_no,
    current_status: "in_store", // set to "in_store" first; installPartInstance() flips it
    current_machine_id: "",
    created_at: serverTimestamp(),
    created_from_request_id,
    source,
  });

  return docRef.id;
}

/**
 * ONBOARDING TOOL — registers a part the company already physically has in
 * store (opening stock, or any part discovered later that was never routed
 * through a spare_part_request). Creates a part_instance directly with
 * status "in_store" — no request, no warranty/PO link needed.
 *
 * Use this ONCE per already-existing part when first setting up the Spare
 * Parts system for a company, or any time Store finds an untracked
 * serialized part sitting on a shelf. Going forward, all NEW parts should
 * still flow through the normal request → resolve → complete pipeline —
 * this function is only a bootstrapping/backfill escape hatch, not a
 * replacement for that governed flow.
 */
export async function createManualPartInstance({
  company_id,
  part_name,
  part_no = "",
  brand = "",
  serial_no,
  created_by,
  created_by_name,
}) {
  if (!company_id) throw new Error("company_id is required");
  if (!part_name) throw new Error("part_name is required");
  if (!serial_no) throw new Error("serial_no is required");

  const part_instance_id = await createPartInstance({
    company_id,
    part_name,
    part_no,
    brand,
    serial_no,
    created_from_request_id: "",
    source: "manual_stock_entry",
  });

  await logPartInstanceHistory({
    company_id,
    part_instance_id,
    event_type: "added_to_stock", // distinct from "installed" — this part was
    // never in a machine, it's opening/backfilled stock sitting in store
    machine_id: "",
    service_id: null,
    done_by: created_by,
    done_by_name: created_by_name,
    notes: "Added as opening/existing stock (manual entry)",
  });

  return part_instance_id;
}

/**
 * ONBOARDING TOOL (installed variant) — registers a part the company
 * already physically has INSTALLED in a machine, but which was never
 * created as a part_instance (legacy installation, pre-dates this
 * system, or came from a Buy/Warranty branch that existed before Spare
 * Parts was built). Without this, fetchInstalledPartInstances() will
 * never find it, and "Send for Repair" can never be triggered for it.
 *
 * Reuses createPartInstance() (in_store first) + installPartInstance()
 * (already-governed, already logs history) — no new write-path duplicated.
 */
export async function registerInstalledPartInstance({
  company_id,
  machine_id,
  part_name,
  part_no = "",
  brand = "",
  serial_no,
  created_by,
  created_by_name,
}) {
  if (!company_id) throw new Error("company_id is required");
  if (!machine_id) throw new Error("machine_id is required");
  if (!part_name) throw new Error("part_name is required");
  if (!serial_no) throw new Error("serial_no is required");

  const part_instance_id = await createPartInstance({
    company_id,
    part_name,
    part_no,
    brand,
    serial_no,
    created_from_request_id: "",
    source: "manual_legacy_install",
  });

  await installPartInstance({
    partInstanceId: part_instance_id,
    company_id,
    machine_id,
    service_id: null,
    done_by: created_by,
    done_by_name: created_by_name,
  });

  return part_instance_id;
}

/**
 * Installs a part_instance into a machine — works for any source
 * (fresh from warranty/buy, "already with us" reuse, or post-repair
 * reinstall). Always logs a history entry.
 */
export async function installPartInstance({ partInstanceId, company_id, machine_id, service_id, done_by, done_by_name }) {
  if (!partInstanceId) throw new Error("partInstanceId is required");
  if (!machine_id) throw new Error("machine_id is required");

  const instance = await fetchPartInstanceById(partInstanceId, company_id); // throws if cross-tenant

  await updateDoc(doc(db, "part_instances", partInstanceId), {
    current_status: "installed",
    current_machine_id: machine_id,
  });

  await logPartInstanceHistory({
    company_id: instance.company_id,
    part_instance_id: partInstanceId,
    event_type: "installed",
    machine_id,
    service_id: service_id || null,
    done_by,
    done_by_name,
    notes: "",
  });
}

/**
 * Removes a part_instance from a machine WITHOUT sending it for repair —
 * e.g. it's simply being taken out and the machine is being scrapped, or
 * moved to store for now with no immediate repair decision.
 * (For the "send for repair" case, use resolveToRepair() instead — it
 * combines removal + repair-status in one governed action.)
 */
export async function removePartInstanceToStore({ partInstanceId, company_id, done_by, done_by_name, notes = "" }) {
  if (!partInstanceId) throw new Error("partInstanceId is required");

  const instance = await fetchPartInstanceById(partInstanceId, company_id); // throws if cross-tenant

  const removedFromMachine = instance.current_machine_id;

  await updateDoc(doc(db, "part_instances", partInstanceId), {
    current_status: "in_store",
    current_machine_id: "",
  });

  await logPartInstanceHistory({
    company_id: instance.company_id,
    part_instance_id: partInstanceId,
    event_type: "removed",
    machine_id: removedFromMachine,
    service_id: null,
    done_by,
    done_by_name,
    notes,
  });
}

export async function scrapPartInstance({ partInstanceId, company_id, done_by, done_by_name, notes = "" }) {
  if (!partInstanceId) throw new Error("partInstanceId is required");

  const instance = await fetchPartInstanceById(partInstanceId, company_id); // throws if cross-tenant

  const removedFromMachine = instance.current_machine_id;

  await updateDoc(doc(db, "part_instances", partInstanceId), {
    current_status: "scrapped",
    current_machine_id: "",
  });

  await logPartInstanceHistory({
    company_id: instance.company_id,
    part_instance_id: partInstanceId,
    event_type: "scrapped",
    machine_id: removedFromMachine,
    service_id: null,
    done_by,
    done_by_name,
    notes,
  });
}

/**
 * Fetch part_instances currently sitting "in_store" that match a given
 * part_name/part_no — used to populate the "Already With Us" picker
 * dropdown in the Store triage UI. company_id always enforced.
 */
export async function fetchAvailablePartInstances({ company_id, part_name, part_no = "" }) {
  if (!company_id) throw new Error("company_id is required");
  if (!part_name) throw new Error("part_name is required");

  const constraints = [
    where("company_id", "==", company_id),
    where("current_status", "==", "in_store"),
    where("part_name", "==", part_name),
  ];

  const snap = await getDocs(query(collection(db, "part_instances"), ...constraints));
  let results = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // part_no match is a soft filter, done client-side — some legacy/manual
  // instances may have it blank, we don't want to hide those unnecessarily
  if (part_no) {
    results = results.filter((r) => !r.part_no || r.part_no === part_no);
  }

  return results;
}

export async function fetchPartInstanceById(partInstanceId, company_id) {
  if (!partInstanceId) throw new Error("partInstanceId is required");
  if (!company_id) throw new Error("company_id is required");
  const snap = await getDoc(doc(db, "part_instances", partInstanceId));
  if (!snap.exists()) throw new Error("Part instance not found");
  const data = { id: snap.id, ...snap.data() };
  assertSameCompany(data, company_id, "part instance");
  return data;
}

/**
 * Fetch part_instances currently "installed" in a specific machine —
 * used by the Repair-branch triage UI to identify which existing
 * (already-installed) part is the one coming out for repair.
 * Optionally narrowed by part_name if the request specifies one.
 */
export async function fetchInstalledPartInstances({ company_id, machine_id, part_name = "" }) {
  if (!company_id) throw new Error("company_id is required");
  if (!machine_id) throw new Error("machine_id is required");

  const constraints = [
    where("company_id", "==", company_id),
    where("current_status", "==", "installed"),
    where("current_machine_id", "==", machine_id),
  ];

  const snap = await getDocs(query(collection(db, "part_instances"), ...constraints));
  let results = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  if (part_name) {
    results = results.filter((r) => r.part_name === part_name);
  }

  return results;
}

// ─────────────────────────────────────────────────────────────────────────
// SECTION 4 — HISTORY (the "diary" — both machine-side and part-side reads
// pull from this same collection, just filtered differently)
// ─────────────────────────────────────────────────────────────────────────

async function logPartInstanceHistory({
  company_id,
  part_instance_id,
  event_type, // "installed" | "removed" | "sent_for_repair" | "returned_from_repair" | "scrapped" | "added_to_stock"
  machine_id,
  service_id,
  done_by,
  done_by_name,
  notes,
}) {
  if (!company_id) throw new Error("company_id is required for part_instance_history (multi-tenant rule)");
  await addDoc(collection(db, "part_instance_history"), {
    company_id,
    part_instance_id,
    event_type,
    machine_id: machine_id || "",
    service_id: service_id || null,
    date: serverTimestamp(),
    done_by,
    done_by_name,
    notes: notes || "",
  });
}

/**
 * Full history of ONE part instance — used by PartInstanceDetail.jsx
 * ("part's own diary" — every machine it has ever been in).
 */
export async function fetchPartInstanceHistory(partInstanceId, company_id) {
  if (!partInstanceId) throw new Error("partInstanceId is required");
  if (!company_id) throw new Error("company_id is required");

  const snap = await getDocs(
    query(
      collection(db, "part_instance_history"),
      where("part_instance_id", "==", partInstanceId),
      where("company_id", "==", company_id)
    )
  );

  const results = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  results.sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
  return results;
}

/**
 * Full part history for ONE machine — used by MachineDetail.jsx's history
 * tab ("machine's own diary" — every part that has ever gone in/out of it).
 * Reads the SAME part_instance_history collection, filtered by machine_id
 * instead of part_instance_id — no separate collection needed.
 */
export async function fetchMachinePartHistory(machineId, company_id) {
  if (!machineId) throw new Error("machineId is required");
  if (!company_id) throw new Error("company_id is required");

  const snap = await getDocs(
    query(
      collection(db, "part_instance_history"),
      where("machine_id", "==", machineId),
      where("company_id", "==", company_id)
    )
  );

  const results = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  results.sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
  return results;
}